/**
 * ShareDoor — the Share door on the picture (plan `2026_10_25_wall_share.md` § 1, board-free: the pill's
 * register). A small surfaced round glyph, top right of the frame, and the menu it opens. The glyph and the
 * menu are two pieces because the phone's frame clips its children: the sheet keeps the menu in its flow
 * (`variant="flow"`), the column floats it under the glyph (`variant="float"`). Which of the two states
 * stands (`open`) belongs to the caller, so Escape and a press outside fold it in the right layer.
 * Every node carries `data-wall-return-exempt`, as the bar's controls do: pressing here is never the
 * return gesture.
 */
import {useEffect, useId, type MutableRefObject, type RefObject} from 'react'
import type {ShareSurface} from '@core/utils/analytics'
import type {ShareMethod} from '@core/wall/share'
import type {ShareWords} from './theme'
import styles from './ShareDoor.module.scss'

export type ShareDoorProps = {
    /** The key of the picture on screen; a change re-prepares the file. */
    imageKey: string
    words: ShareWords
    /** The menu's rows, in order. */
    methods: readonly ShareMethod[]
    /** Fetch the picture into a file ahead of the tap (cached per key). */
    prepare: () => void
    /** A press on the glyph: `native` = the system sheet is opening, `menu` = the caller opens the menu. */
    onTap: (surface: ShareSurface) => 'native' | 'menu'
    onPick: (method: ShareMethod, surface: ShareSurface) => void
}

const ROW_WORD: Record<ShareMethod, keyof Pick<ShareWords, 'native' | 'download' | 'copyImage' | 'copyLink'>> = {
    native: 'native',
    download: 'download',
    copy_image: 'copyImage',
    copy_link: 'copyLink',
}

export function ShareDoor({door, surface, open, onOpenChange, menuId}: {
    door: ShareDoorProps
    surface: ShareSurface
    open: boolean
    onOpenChange: (open: boolean) => void
    menuId: string
}) {
    const {imageKey, prepare} = door
    useEffect(() => {
        prepare()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- a new picture is the only trigger
    }, [imageKey])
    return (
        <button
            type="button"
            className={styles.door}
            aria-label={door.words.door}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? menuId : undefined}
            data-wall-return-exempt="true"
            data-share-door="true"
            onClick={() => {
                if (open) {
                    onOpenChange(false)
                    return
                }
                if (door.onTap(surface) === 'menu') onOpenChange(true)
            }}
        >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 15V3" />
                <path d="m7 8 5-5 5 5" />
                <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
            </svg>
        </button>
    )
}

export function ShareMenu({door, surface, onOpenChange, menuId, variant}: {
    door: ShareDoorProps
    surface: ShareSurface
    onOpenChange: (open: boolean) => void
    menuId: string
    variant: 'float' | 'flow'
}) {
    return (
        <div
            id={menuId}
            role="menu"
            className={`${styles.menu} ${variant === 'float' ? styles.float : styles.flow}`}
            data-wall-return-exempt="true"
            data-share-door="true"
        >
            {door.methods.map((method) => (
                <button
                    key={method}
                    type="button"
                    role="menuitem"
                    className={styles.item}
                    data-wall-return-exempt="true"
                    onClick={() => {
                        onOpenChange(false)
                        door.onPick(method, surface)
                    }}
                >
                    {door.words[ROW_WORD[method]]}
                </button>
            ))}
        </div>
    )
}

/** One id for the glyph's `aria-controls` and the menu. */
export function useShareMenuId(): string {
    return useId()
}

/**
 * A press outside the door and its menu (the `data-share-door` nodes) folds the menu and changes nothing else — the way out the ratio
 * row already has (`Column.tsx`). A press on the scrim is raised in `scrimFolded`, so the click that follows
 * it does not also return the wall: one layer at a time.
 */
export function useFoldOnOutsidePress(args: {
    open: boolean
    scrimRef: RefObject<HTMLElement | null>
    scrimFolded: MutableRefObject<boolean>
    fold: () => void
}) {
    const {open, scrimRef, scrimFolded, fold} = args
    useEffect(() => {
        if (!open) return
        function onPointerDown(e: PointerEvent) {
            if (e.target instanceof Element && e.target.closest('[data-share-door]')) return
            fold()
            if (e.target === scrimRef.current) scrimFolded.current = true
        }
        document.addEventListener('pointerdown', onPointerDown, true)
        return () => document.removeEventListener('pointerdown', onPointerDown, true)
    }, [open, scrimRef, scrimFolded, fold])
}
